// src/components/Footer.js
import React from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  MessageCircle,
} from 'lucide-react';
import logo from '../assets/logo-white.png';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-slate-950 text-white py-16 border-t border-slate-800">
      <div className="container mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Logo y descripción */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center md:text-left"
          >
            <img
              src={logo}
              alt="TekNet"
              className="h-20 mx-auto md:mx-0 mb-6"
            />
            <p className="text-slate-400 text-lg leading-relaxed max-w-xs mx-auto md:mx-0">
              {t('footer_description')}
            </p>
          </motion.div>

          {/* Contacto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-center md:text-left"
          >
            <h3 className="text-2xl font-semibold mb-6 text-orange-400">
              {t('footer_contact_title')}
            </h3>

            <div className="space-y-4 text-slate-300">
              <a
                href="mailto:info@teknetpr.com"
                className="flex items-center justify-center md:justify-start gap-3 hover:text-orange-400 transition"
              >
                <Mail className="w-5 h-5" />
                <span>info@teknetpr.com</span>
              </a>

              <a
                href="tel:+17875453020"
                className="flex items-center justify-center md:justify-start gap-3 hover:text-orange-400 transition"
              >
                <Phone className="w-5 h-5" />
                <span>+1 (787) 545-3020</span>
              </a>

              <div className="flex items-center justify-center md:justify-start gap-3">
                <MapPin className="w-5 h-5" />
                <span>{t('footer_location')}</span>
              </div>
            </div>
          </motion.div>

          {/* Redes sociales */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-center md:text-left"
          >
            <h3 className="text-2xl font-semibold mb-6 text-orange-400">
              {t('footer_follow_title')}
            </h3>

            <div className="flex gap-6 justify-center md:justify-start">
              <a
                href="https://facebook.com/teknetpr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-slate-800/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500 border border-slate-700 transition-all duration-300"
              >
                <Facebook className="w-6 h-6" />
              </a>

              <a
                href="https://instagram.com/teknetpr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-slate-800/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500 border border-slate-700 transition-all duration-300"
              >
                <Instagram className="w-6 h-6" />
              </a>

              <a
                href="https://wa.me/17875453020"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-slate-800/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500 border border-slate-700 transition-all duration-300"
              >
                <MessageCircle className="w-6 h-6" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Línea separadora y copyright */}
        <div className="border-t border-slate-800 pt-8 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()}{' '}
            <span className="text-orange-400 font-bold">TekNet</span>.{' '}
            {t('footer_rights')}
          </p>

          <p className="text-sm text-slate-500 mt-2">
            {t('footer_tagline')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
